import * as Config from '../../config';
import { default as Locale } from '../../utils/Locale';
import * as UUID from '../../utils/Uuid';
import { AlertInfo, AlertType, AlertRecord, AlertCategories } from "../Types";
import { AlertLogic } from './AlertLogic';
import { PortTypeNames } from '../../config/Types';
import { ContentCategories } from "../../types";
import logger from "../../utils/Logger";


export class NoFlow extends AlertLogic {
    static type = AlertType.NoFlow;
    static category = AlertCategories.EQUIPMENT_FAILURE;
    static contentCategories = [ ContentCategories.WATER_FLOW ];
    static title = Locale.gettext('No Flow');

    getInfo(alert: AlertRecord = this.alert, config: Config.AllConfig = this.allConfigs): AlertInfo {
        // Same for all alert types
        const { body, device, alertData, firstReported } = this.getCommons(alert, config);

        // Varying across alert types
        const cardIllustration = 'status_problem';
        const detailIcon = 'flowsensor';
        const flowSwitchList = [];
        const flowSwitchTextList = [];
        for (const sw of (alertData?.NoFlowFlowSwitchUUIDs || [])) {
            const swDev = config.Config?.Devices?.find(dev => dev.UUID === sw);
            if (!swDev) continue;

            const devicePort = swDev.HardwareDevice.Ports && swDev.HardwareDevice.Ports.length > 0 ? 
                swDev.HardwareDevice.Ports[0] : 
                { Type: 'Unknown', Number: 0 }
            ;
            flowSwitchList.push(swDev);
            flowSwitchTextList.push("\"" + swDev.Name + "\" (" + PortTypeNames[devicePort.Type] + " Port " + devicePort.Number + ")");
        }

        const disabledDeviceList = [];
        const disabledDeviceTextList = [];
        for (const sw of (alertData?.DisabledDeviceUUIDs || [])) {
            const swDev = config.Config?.Devices?.find(dev => dev.UUID === sw);
            if (!swDev) continue;

            disabledDeviceList.push(swDev);
            disabledDeviceTextList.push(swDev.Name);
        }

        const alertInfo = AlertInfo.fromCardConfig({
            cardTitle: this.locale.gettext('Insufficient Water Flow'),
            cardDescription: this.locale.gettext('Heating, cooling, and chemistry devices have been disabled!'),
            cardIllustration: cardIllustration,
            detailIcon: detailIcon,
            detailDescriptionArr: [
                this.locale.ngettext(
                    'The flow sensor %2$s is reporting insufficient flow.', 
                    'The flow sensors %2$s are reporting insufficient flow.', 
                    flowSwitchTextList.length,
                    flowSwitchTextList.join(', ')
                ),
                this.locale.gettext('Please check for the following:'),
                (
                    " - " + this.locale.gettext('A clogged or dirty filter.') + "\n" +
                    " - " + this.locale.gettext('An obstruction in the skimmer basket, pump strainer, or pipes.') + "\n" +
                    " - " + this.locale.gettext('Failure for the pump to prime due to a poorly seated lid.') + "\n" +
                    " - " + this.locale.gettext('Incorrectly set valve positions.') + "\n" +
                    " - " + this.locale.gettext('No power to pump or pump failure.') + "\n" +
                    " - " + this.locale.gettext('Failure of the flow sensor itself.')
                ),
                this.locale.gettext('Heating, cooling, and chemistry devices will be disabled for safety!'),
                disabledDeviceTextList.length === 0
                    ? this.locale.gettext('Devices will not work until flow is restored.')
                    : this.locale.ngettext(
                        '%2$s will not work until flow is restored.',
                        '%2$s will not work until flow is restored.',
                        disabledDeviceTextList.length,
                        disabledDeviceTextList.join(', ')
                    ),
            ],
            actionButtonTitle: false,
            resolveSummary: this.locale.gettext('Flow restored')
        });
        alertInfo.contentCategories = NoFlow.contentCategories;
        return alertInfo;
    } 

    static mock(type:AlertType, config:Config.AllConfig):AlertRecord {
        const device = config.Config.Devices.find(dev => dev.DeviceType === Config.DeviceTypes.PUMP);
        if (!device) throw new Error(`Create a Pump to mock ${type} alert.`);

        const data: { [ key: string ]: any } = {};

        const swDevices = config.Config.Devices.filter(dev => dev.DeviceType === Config.DeviceTypes.FLOWSWITCH);
        if (swDevices.length) {
            data.NoFlowFlowSwitchUUIDs = swDevices.map(dev => dev.UUID);
        } else {
            logger.warn(`Create a Flow Swtich to better mock ${type} alert.`);
        }

        const heaters = config.Config.Devices.filter(dev => [ Config.DeviceTypes.HEATER, Config.DeviceTypes.CHILLER ].includes(dev.DeviceType));
        if (heaters.length) {
            data.DisabledDeviceUUIDs = heaters.map(dev => dev.UUID);
        } else {
            logger.warn(`Create a Heater or Chiller to better mock ${type} alert.`);
        }
        
        return new AlertRecord({
            id: Math.floor(Math.random() * 100000),
            uuid: UUID.modifySection(4, UUID.modifySection(3, device.UUID, "MOCK"), "ALRT" + type),
            siteId: Math.floor(Math.random() * 100000),
            deviceId: 1,
            entityUuid: device.UUID,
            bodyUuid: config.Config.BodiesOfWater[0]?.UUID,
            type,
            data,
        });
    }
}